<template>
  <div class="hunan-map">
    <div class="map-container">
      <div ref="mapRef" class="map-chart" :style="mapChartStyle"></div>
      
      <!-- 导航按钮（仅省级下钻到城市时显示） -->
      <div class="map-navigation" v-if="mapLevel === 'city' && !enteredDirectCity">
        <button 
          class="nav-button back-to-province" 
          @click="goBackToProvince"
          :disabled="isMapLoading"
        >
          <span class="nav-icon">←</span>
          <span class="nav-text">返回省级</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import * as echarts from 'echarts'
import hunanGeo from 'china-geojson/src/geojson/hu_nan_geo.json'
import { getMapConfig } from './mapRegion/index.js'

export default {
  name: 'HunanMap',
  props: {
    // 微调参数：背景与地图整体偏移（像素）
    dx: { type: Number, default: 0 },
    dy: { type: Number, default: 0 }
  },
  data() {
    return {
      chart: null,
      currentAdcode: '430000', // 默认湖南省
      currentMapConfig: null,
      currentGeoData: null,
      // 背景图片加载控制
      currentBgUrl: null,
      _bgLoading: false,
      // 地图层级状态管理
      mapLevel: 'province', // 'province' | 'city'
      navigationHistory: [], // 浏览路径记录
      // 入口标记：是否通过URL直接进入城市视图
      enteredDirectCity: false,
      // 路由变化来源：'drill' 表示由下钻触发，其它为外部/手动变化
      _routeChangeOrigin: null,
      // 性能优化缓存
      _geoAspectCache: null,
      _imageFitCache: new Map(),
      _coordMapCache: {},
      _dataByNameCache: {},
      _tooltipPixelCache: {},
      _tooltipHtmlCache: {},
      _lastUpdateTime: 0,
      _debounceTimer: null,
      // 新增：地图数据缓存和加载状态
      _geoDataCache: new Map(), // 缓存已加载的地图数据
      _mapConfigCache: new Map(), // 缓存地图配置
      _loadingStates: new Map(), // 跟踪各地区的加载状态
      _routeChangeTimer: null, // 路由变化防抖定时器
      isMapLoading: false, // 地图加载状态
      // 悬停状态记录
      _currentHoverState: null, // 当前悬停的地区信息 { name, coord }
      // 预加载控制
      _hasPreloadedCityAssets: false,
      _preloadInProgress: false,
    }
  },
  computed: {
    ...mapState(['loading']),
    ...mapState('map', ['hunanMapData']),
    ...mapState('region', ['currentRegion']),
    finalBgUrl () {
      if (this.currentMapConfig && this.currentMapConfig.backgroundImage) {
        // 优先使用webp格式
        try {
          return require(`@/assets/images/${this.currentMapConfig.backgroundImage.webp}`)
        } catch (e) {
          // 如果webp不存在，使用png
          return require(`@/assets/images/${this.currentMapConfig.backgroundImage.png}`)
        }
      }
      return require('@/assets/images/hunan_map.png');
    },
    tooltipBgUrl () {
      return require('@/assets/images/tooltip_bg.png');
    },
    // 动态获取当前地图配置的样式
    mapChartStyle () {
      const defaultStyle = {
        width: '775px',
        height: '775px',
        marginTop: 'auto'
      }
      
      if (this.currentMapConfig && this.currentMapConfig.echartsMapStyle) {
        return { ...defaultStyle, ...this.currentMapConfig.echartsMapStyle }
      }
      
      return defaultStyle
    },
  },
  async mounted() {
    // 确保DOM渲染完成后再初始化
    await this.$nextTick()
    await this.initAdcodeFromUrl()
    await this.initChart()
    // updateChart 已在 initAdcodeFromUrl 中调用，避免重复调用
  },
  beforeDestroy() {
    // 清理定时器
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer)
      this._debounceTimer = null
    }
    if (this._routeChangeTimer) {
      clearTimeout(this._routeChangeTimer)
      this._routeChangeTimer = null
    }
    
    // 清理图表
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    
    // 清理缓存
    if (this._geoDataCache) {
      this._geoDataCache.clear()
    }
    if (this._mapConfigCache) {
      this._mapConfigCache.clear()
    }
    if (this._loadingStates) {
      this._loadingStates.clear()
    }
    if (this._imageFitCache && typeof this._imageFitCache.clear === 'function') {
      this._imageFitCache.clear()
    }
    
    // 重置状态
    this.isMapLoading = false
  },
  watch: {
    dx() { this.updateBgGraphic() },
    dy() { this.updateBgGraphic() },
    '$route': {
      handler(newRoute, oldRoute) {
        const newAdcode = newRoute.query.adcode || '430000'
        const oldAdcode = oldRoute?.query?.adcode || this.currentAdcode
        // 根据来源标记更新入口逻辑：下钻进入城市则显示返回按钮；手动/外部直达城市则不显示
        const origin = this._routeChangeOrigin
        this._routeChangeOrigin = null
        if (origin === 'drill') {
          this.enteredDirectCity = false
        } else {
          this.enteredDirectCity = (newAdcode !== '430000')
        }
        // 立即同步地图层级，避免在省级加载期间按钮短暂显示
        this.updateMapLevel(newAdcode)
        
        // 如果adcode没有变化，直接返回
        if (newAdcode === oldAdcode) {
          return
        }
        
        // 清除之前的定时器
        if (this._routeChangeTimer) {
          clearTimeout(this._routeChangeTimer)
        }
        
        // 防抖处理，避免快速切换时的重复请求
        this._routeChangeTimer = setTimeout(async () => {
          try {
            // 设置加载状态
            this.isMapLoading = true
            
            // 更新当前adcode
            this.currentAdcode = newAdcode
            
            // 更新store中的区域状态（仅在非drillToMap触发的路由变化时）
            const mapConfig = getMapConfig(newAdcode)
            const level = newAdcode === '430000' ? 'province' : 'city'
            if (origin !== 'drill') {
              await this.switchRegion({
                level,
                name: mapConfig.name,
                mapName: mapConfig.mapName,
                adcode: newAdcode
              })
            } else {
              // 下钻流程中，区域已在 drillToMap 内更新，这里仅确保 currentRegion 同步即可
              await this.updateCurrentRegion({
                level,
                name: mapConfig.name,
                mapName: mapConfig.mapName,
                adcode: newAdcode
              })
            }
            
            // 加载地图配置和数据
            await this.loadMapConfigWithCache()
            
            // 强制重新渲染图表
            await this.$nextTick()
            if (this.chart) {
              // 清除悬停状态与提示，避免位置和状态残留
              this._currentHoverState = null
              this.chart.dispatchAction({ type: 'hideTip' })
              this.chart.setOption({ series: [{ id: 'hover-point', type: 'effectScatter', coordinateSystem: 'geo', data: [] }] }, false)
              this.chart.clear()
              await this.updateChart()
            }
          } catch (error) {
            console.error('路由变化处理失败:', error)
            // 错误时回退到之前的adcode
            if (oldAdcode && oldAdcode !== newAdcode) {
              this.$router.replace({ 
                query: { ...this.$route.query, adcode: oldAdcode } 
              }).catch(() => {})
            }
          } finally {
            this.isMapLoading = false
            // 下钻或返回省级完成：恢复外层自动刷新
            if (origin === 'drill') {
              this.$emit('resume-auto-refresh')
            }
          }
        }, 150) // 150ms防抖延迟
      },
      immediate: true
    },
    'hunanMapData.regions': {
      handler() {
        if (this.hunanMapData.regions && Array.isArray(this.hunanMapData.regions)) {
          this.updateChart()
        }
      },
      deep: true
    },
    'hunanMapData.totals': {
      handler() {
        // 总码洋变更时覆盖层文案自动更新
      },
      deep: true
    },
    // 监听样式变化，确保尺寸变更时触发resize
    mapChartStyle: {
      handler(newStyle, oldStyle) {
        if (newStyle && oldStyle && this.chart) {
          // 使用nextTick确保DOM更新完成后再resize
          this.$nextTick(() => {
            if (this.chart) {
              this.chart.resize()
            }
          })
        }
      },
      deep: true
    },
  },
  methods: {
    ...mapActions(['switchRegion']),
    ...mapActions('region', ['updateCurrentRegion']),
    // 处理地图点击事件
    handleMapClick(params) {
      // 防止重复点击和加载中的点击
      if (this.isMapLoading) {
        return
      }
      
      // 只在省级地图时支持下钻
      if (this.mapLevel !== 'province') {
        return
      }
      
      // 检查是否点击了有效的地图区域
      if (!params || !params.data) {
        return
      }
      
      const clickedAdcode = this.currentMapConfig.cityAdcodeMap[params.data.name]
      const clickedName = params.data.name
      
      // 检查adcode是否有效
      if (!clickedAdcode || typeof clickedAdcode !== 'string') {
        console.warn('点击区域缺少有效的adcode:', params.data)
        return
      }
      
      // 确保不是点击省级区域本身
      if (clickedAdcode === '430000') {
        return
      }
      
      // 检查是否有对应的市级地图配置
      try {
        const mapConfig = getMapConfig(clickedAdcode)
        if (mapConfig && mapConfig.name) {
          this.drillToMap(clickedAdcode, true)
        } else {
          console.warn(`${clickedName}的地图配置无效，adcode: ${clickedAdcode}`)
        }
      } catch (error) {
        console.warn(`未找到${clickedName}的地图配置，adcode: ${clickedAdcode}`, error)
      }
    },
    
    // 地图层级管理方法
    updateMapLevel(adcode) {
      if (adcode === '430000') {
        this.mapLevel = 'province'
      } else {
        this.mapLevel = 'city'
      }
    },
    
    // 添加导航历史记录
    addToNavigationHistory(adcode, name) {
      const historyItem = {
        adcode,
        name,
        timestamp: Date.now()
      }
      
      // 避免重复添加相同的记录
      const lastItem = this.navigationHistory[this.navigationHistory.length - 1]
      if (!lastItem || lastItem.adcode !== adcode) {
        this.navigationHistory.push(historyItem)
      }
    },
    
    // 返回上一级地图
    goBackToPrevious() {
      if (this.navigationHistory.length > 1) {
        // 移除当前项
        this.navigationHistory.pop()
        // 获取上一项
        const previousItem = this.navigationHistory[this.navigationHistory.length - 1]
        if (previousItem) {
          this.drillToMap(previousItem.adcode, false) // false表示不添加到历史记录
        }
      }
    },
    
    // 返回省级地图
    goBackToProvince() {
      this.navigationHistory = [{ adcode: '430000', name: '湖南省', timestamp: Date.now() }]
      this.drillToMap('430000', false)
    },
    
    // 下钻到指定地图
    async drillToMap(targetAdcode, addToHistory = true) {
      if (this.isMapLoading) return
      
      try {
        // 下钻/返回省级开始：通知上层暂停自动刷新
        this.$emit('pause-auto-refresh')
        this.isMapLoading = true
        
        // 清除悬停状态和水波纹效果
        this._currentHoverState = null
        if (this.chart) {
          // 清除水波纹效果
          this.chart.setOption({
            series: [{ id: 'hover-point', type: 'effectScatter', coordinateSystem: 'geo', data: [] }]
          })
          
          // 清除tooltip提示框
          this.chart.dispatchAction({
            type: 'hideTip'
          })
        }
        
        // 添加到导航历史
        if (addToHistory) {
          const mapConfig = getMapConfig(targetAdcode)
          this.addToNavigationHistory(targetAdcode, mapConfig.name)
        }
        
        // 更新地图层级
        this.updateMapLevel(targetAdcode)
        // 只要是从省级下钻到城市，就认为是非直达入口，从而显示返回省级按钮
        if (targetAdcode !== '430000') {
          this.enteredDirectCity = false
        }
        
        // 添加淡出动画
        if (this.chart) {
          this.chart.setOption({
            animation: true,
            animationDuration: 150,
            animationEasing: 'cubicOut',
            series: [{
              type: 'map',
              itemStyle: {
                opacity: 0
              }
            }]
          })
        }
        
        // 等待淡出动画完成
        await new Promise(resolve => setTimeout(resolve, 150))
        
        // 更新store中的区域状态
        const mapConfig = getMapConfig(targetAdcode)
        const level = targetAdcode === '430000' ? 'province' : 'city'
        await this.switchRegion({
          level,
          name: mapConfig.name,
          mapName: mapConfig.mapName,
          adcode: targetAdcode
        })
        
        // 更新路由
        // 标记此次路由变化由下钻触发
        this._routeChangeOrigin = 'drill'
        this.$router.push({
          query: { ...this.$route.query, adcode: targetAdcode }
        }).catch(() => {})
        
      } catch (error) {
        console.error('地图下钻失败:', error)
        
        // 错误处理：恢复地图层级和导航历史
        if (addToHistory && this.navigationHistory.length > 0) {
          this.navigationHistory.pop() // 移除刚添加的错误记录
        }
        
        // 恢复地图可见性（如果已经开始淡出）
        if (this.chart) {
          this.chart.setOption({
            series: [{
              type: 'map',
              itemStyle: {
                opacity: 1
              }
            }]
          })
        }
        
        throw error
      } finally {
        // 延迟重置加载状态，确保动画完成
        setTimeout(() => {
          this.isMapLoading = false
        }, 300)
      }
    },
    
    // 从URL初始化adcode
    async initAdcodeFromUrl() {
      const adcode = this.$route.query.adcode || '430000'
      this.currentAdcode = adcode
      this.isMapLoading = true
      // 标记是否通过URL直接进入城市视图
      this.enteredDirectCity = (adcode !== '430000')
      
      // 初始化store中的区域状态
      const mapConfig = getMapConfig(adcode)
      const level = adcode === '430000' ? 'province' : 'city'
      await this.updateCurrentRegion({
        level,
        name: mapConfig.name,
        mapName: mapConfig.mapName,
        adcode
      })
      
      // 初始化导航历史和地图层级
      this.updateMapLevel(adcode)
      
      // 如果是省级地图，初始化导航历史
      if (adcode === '430000') {
        this.navigationHistory = [{ adcode: '430000', name: '湖南省', timestamp: Date.now() }]
      } else {
        // 如果直接访问城市地图，确保导航历史包含省级和当前城市
        const mapConfig = getMapConfig(adcode)
        this.navigationHistory = [
          { adcode: '430000', name: '湖南省', timestamp: Date.now() },
          { adcode, name: mapConfig.name, timestamp: Date.now() }
        ]
      }
      
      try {
        await this.loadMapConfigWithCache()
        // 确保页面初始化时正确渲染
        await this.$nextTick()
        if (this.chart) {
          await this.updateChart()
        }
        // 首次通过 URL 进入省级时，异步预加载各市资源以提升下钻流畅度
        if (adcode === '430000' && !this._hasPreloadedCityAssets) {
          this.schedulePreloadCityAssets()
        }
      } catch (error) {
        console.error('初始化地图失败:', error)
      } finally {
        this.isMapLoading = false
      }
    },
    
    // 带缓存的地图配置加载方法
    async loadMapConfigWithCache() {
      const adcode = this.currentAdcode
      
      // 确保缓存对象已初始化
      if (!this._loadingStates) this._loadingStates = new Map()
      if (!this._mapConfigCache) this._mapConfigCache = new Map()
      if (!this._geoDataCache) this._geoDataCache = new Map()
      
      // 检查是否正在加载中
      if (this._loadingStates.get(adcode)) {
        return
      }
      
      try {
        // 设置加载状态
        this._loadingStates.set(adcode, true)
        
        // 检查配置缓存
        let mapConfig = this._mapConfigCache.get(adcode)
        if (!mapConfig) {
          mapConfig = getMapConfig(adcode)
          this._mapConfigCache.set(adcode, mapConfig)
        }
        this.currentMapConfig = mapConfig
        
        // 检查地图数据缓存
        let geoData = this._geoDataCache.get(adcode)
        if (!geoData) {
          geoData = await this.loadGeoDataWithCache(adcode, mapConfig)
          this._geoDataCache.set(adcode, geoData)
        }
        this.currentGeoData = geoData
        
        // 清除相关缓存，确保重新计算
        this._geoAspectCache = null
        if (this._imageFitCache && typeof this._imageFitCache.clear === 'function') {
          this._imageFitCache.clear()
        } else {
          this._imageFitCache = new Map()
        }
        this._coordMapCache = {}
        this._dataByNameCache = {}
        this._tooltipPixelCache = {}
        this._tooltipHtmlCache = {}
        
        // 如果图表已存在，强制重新渲染
        if (this.chart) {
          this.chart.clear()
          await this.updateChart()
        }
      } catch (error) {
        console.error(`加载地图配置失败 (${adcode}):`, error)
        
        // 从缓存中移除失败的数据
        if (this._mapConfigCache) this._mapConfigCache.delete(adcode)
        if (this._geoDataCache) this._geoDataCache.delete(adcode)
        
        // 回退到默认配置
        if (adcode !== '430000') {
          this.currentAdcode = '430000'
          this.currentMapConfig = getMapConfig('430000')
          this.currentGeoData = hunanGeo
          
          // 确保缓存重新初始化
          if (!this._imageFitCache || typeof this._imageFitCache.clear !== 'function') {
            this._imageFitCache = new Map()
          }
          if (this.chart) {
            this.chart.clear()
            this.updateChart()
          }
        }
        throw error
      } finally {
        // 清除加载状态
        if (this._loadingStates) {
          this._loadingStates.set(adcode, false)
        }
      }
    },
    
    // 带缓存的地图数据加载方法
    async loadGeoDataWithCache(adcode, mapConfig) {
      try {
        if (adcode === '430000') {
          // 湖南省使用内置数据
          return hunanGeo
        } else {
          // 其他城市从public目录加载
          const geoJsonPath = mapConfig.geoJsonPath
          const response = await fetch(`./${geoJsonPath}`)
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}, url: ${geoJsonPath}`)
          }
          
          const geoData = await response.json()
          return geoData
        }
      } catch (error) {
        console.error(`加载地图数据失败 (${adcode}):`, error)
        // 回退到湖南省数据
        return hunanGeo
      }
    },
    
    async initChart() {
      // 确保DOM元素存在
      if (!this.$refs.mapRef) {
        console.error('地图容器DOM元素不存在')
        return
      }
      
      // 如果图表已存在，先销毁
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
      
      this.chart = echarts.init(this.$refs.mapRef)
      
      // 注册地图
      if (this.currentGeoData) {
        echarts.registerMap(this.currentMapConfig?.name || 'hunan', this.currentGeoData);
      } else {
        echarts.registerMap('hunan', hunanGeo);
      }
      
      const resizeHandler = () => {
        if (this.chart && !this.loading) {
          this.chart.resize()
          this.updateBgGraphic()
        }
      }
      
      window.addEventListener('resize', resizeHandler)
      
      // 清理事件监听器
      this.$once('hook:beforeDestroy', () => {
        window.removeEventListener('resize', resizeHandler)
      })
    },
    // 预加载：调度在空闲时间执行，避免影响首屏渲染
    schedulePreloadCityAssets() {
      if (this._hasPreloadedCityAssets || this._preloadInProgress || this.mapLevel !== 'province') return
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 300))
      idle(() => {
        this.preloadCityAssets({ maxConcurrent: 3 })
      })
    },
    // 并发预加载城市的 geoJSON 与背景图片
    async preloadCityAssets({ maxConcurrent = 3 } = {}) {
      if (this._hasPreloadedCityAssets || this._preloadInProgress) return
      this._preloadInProgress = true
      try {
        const adcodes = this.getAllCityAdcodes()
        if (!adcodes || !adcodes.length) {
          this._hasPreloadedCityAssets = true
          return
        }
        const tasks = adcodes.map((code) => async () => {
          try {
            const cfg = getMapConfig(code)
            // 预加载背景图片（若存在配置）
            const imgUrl = this.resolveBackgroundUrl(cfg)
            if (imgUrl) {
              await this.preloadImage(imgUrl)
            }
          } catch (e) {
            // 预加载失败不影响主流程，记录即可
            console.warn('城市资源预加载失败:', code, e)
          }
          // 微小让步，避免主线程长时间占用
          await new Promise((r) => setTimeout(r, 10))
        })
        await this.runWithConcurrency(tasks, Math.min(maxConcurrent, 4))
        this._hasPreloadedCityAssets = true
      } finally {
        this._preloadInProgress = false
      }
    },
    // 获取所有城市 adcode 列表
    getAllCityAdcodes() {
      const provinceCfg = this.currentMapConfig?.cityAdcodeMap ? this.currentMapConfig : getMapConfig('430000')
      const map = provinceCfg?.cityAdcodeMap || {}
      const codes = Object.values(map).filter((c) => typeof c === 'string' && c !== '430000')
      // 去重
      return Array.from(new Set(codes))
    },
    // 解析背景图片 URL（优先 webp）
    resolveBackgroundUrl(cfg) {
      try {
        if (cfg?.backgroundImage?.webp) {
          return require(`@/assets/images/${cfg.backgroundImage.webp}`)
        }
      } catch (_) {}
      try {
        if (cfg?.backgroundImage?.png) {
          return require(`@/assets/images/${cfg.backgroundImage.png}`)
        }
      } catch (_) {}
      return null
    },
    // 预加载图片到浏览器缓存
    preloadImage(src) {
      return new Promise((resolve, reject) => {
        if (!src) return resolve()
        const img = new Image()
        img.onload = () => resolve()
        img.onerror = (e) => reject(e)
        img.src = src
      })
    },
    // 简易并发执行器
    async runWithConcurrency(tasks, max) {
      let index = 0
      const workers = Array.from({ length: Math.max(1, max) }).map(async () => {
        while (index < tasks.length) {
          const i = index++
          await tasks[i]()
        }
      })
      await Promise.all(workers)
    },
    // 计算 GeoJSON 的经纬度包围盒纵横比（近似），用于校正宽度
    calcGeoAspect() {
      if (this._geoAspectCache) return this._geoAspectCache
      
      const geoData = this.currentGeoData || hunanGeo
      const extent = { minLng: Infinity, maxLng: -Infinity, minLat: Infinity, maxLat: -Infinity }
      const update = (pt) => {
        const [lng, lat] = pt
        if (typeof lng !== 'number' || typeof lat !== 'number') return
        extent.minLng = Math.min(extent.minLng, lng)
        extent.maxLng = Math.max(extent.maxLng, lng)
        extent.minLat = Math.min(extent.minLat, lat)
        extent.maxLat = Math.max(extent.maxLat, lat)
      }
      const walk = (coords) => {
        if (!coords) return
        if (Array.isArray(coords)) {
          if (coords.length && typeof coords[0] === 'number') {
            update(coords)
          } else {
            coords.forEach(walk)
          }
        }
      }
      (geoData.features || []).forEach(f => walk(f.geometry && f.geometry.coordinates))
      const lngSpan = extent.maxLng - extent.minLng
      const latSpan = extent.maxLat - extent.minLat
      const rGeo = (lngSpan > 0 && latSpan > 0) ? (lngSpan / latSpan) : (3606 / 3618)
      this._geoAspectCache = rGeo
      return rGeo
    },
    // 计算在当前画布中按原图比例适配后的宽高与偏移
    calcImageFit() {
      const cw = this.chart.getWidth()
      const ch = this.chart.getHeight()
      
      // 基于容器尺寸和偏移量的缓存键
      const cacheKey = `${cw}-${ch}-${this.dx}-${this.dy}-${this.currentAdcode}`
      if (this._imageFitCache && this._imageFitCache.key === cacheKey) {
        return this._imageFitCache.data
      }
      
      // 使用当前地图配置的图片尺寸，如果没有则使用默认值
      const IMG_W = this.currentMapConfig?.imageSize?.width || 3606
      const IMG_H = this.currentMapConfig?.imageSize?.height || 3618
      const rImg = IMG_W / IMG_H
      const rCont = cw / ch
      let drawW, drawH, offsetX = 0, offsetY = 0
      
      if (rCont > rImg) {
        // 以高度适配
        drawH = ch
        drawW = Math.round(drawH * rImg)
        offsetX = Math.round((cw - drawW) / 2)
      } else {
        // 以宽度适配
        drawW = cw
        drawH = Math.round(drawW / rImg)
        offsetY = Math.round((ch - drawH) / 2)
      }
      
      // 通过 GeoJSON 纵横比校正宽度，使地图宽度与背景绘制宽度一致
      const rGeo = this.calcGeoAspect()
      let aspectScale = (drawW / drawH) / rGeo
      let layoutSize = drawH
      
      // 个别地图特殊处理：独立调整参数（长沙、怀化）
      const specialConfig = this.currentMapConfig?.specialConfig
      if (specialConfig) {
        // 独立调整 layoutSize
        if (specialConfig.layoutSizeAdjustment && specialConfig.layoutSizeAdjustment.enabled) {
          const adjustment = specialConfig.layoutSizeAdjustment
          let adjustedLayoutSize = layoutSize * adjustment.multiplier
          
          // 应用最小最大限制
          adjustedLayoutSize = Math.max(adjustment.minSize, Math.min(adjustment.maxSize, adjustedLayoutSize))
          layoutSize = adjustedLayoutSize          
        }
        
        // 独立调整 aspectScale
        if (specialConfig.aspectScaleAdjustment && specialConfig.aspectScaleAdjustment.enabled) {
          const adjustment = specialConfig.aspectScaleAdjustment
          let adjustedAspectScale = aspectScale * adjustment.multiplier
          
          // 应用最小最大限制
          adjustedAspectScale = Math.max(adjustment.minScale, Math.min(adjustment.maxScale, adjustedAspectScale))
          aspectScale = adjustedAspectScale          
        }
        
        // 应用位置偏移补偿
        if (specialConfig.positionOffset) {
          offsetX += Math.round(cw * specialConfig.positionOffset.x)
          offsetY += Math.round(ch * specialConfig.positionOffset.y)
        }
      }
      
      // 返回容器尺寸，便于将像素偏移换算为 geo 的百分比中心
      const result = { cw, ch, drawW, drawH, offsetX, offsetY, layoutSize, aspectScale }
      
      // 缓存计算结果
      this._imageFitCache = { key: cacheKey, data: result }
      return result
    },
    async updateChart() {
      if (!this.chart || !this.hunanMapData.regions || !Array.isArray(this.hunanMapData.regions) || !this.hunanMapData.regions.length) return;

      // 如果正在加载中，延迟更新
      if (this.isMapLoading) {
        setTimeout(() => this.updateChart(), 100)
        return
      }

      // 防抖快速更新以防止性能问题
      const now = Date.now()
      if (now - this._lastUpdateTime < 100) {
        if (this._debounceTimer) clearTimeout(this._debounceTimer)
        this._debounceTimer = setTimeout(() => this.updateChart(), 100)
        return
      }
      this._lastUpdateTime = now

      // 在渲染前确保背景图片已预加载，避免切换时出现背景延迟导致动画不协调
      await this.ensureBgPreloaded()

      // 添加过渡效果：先淡出当前内容
      if (this.chart) {
        this.chart.setOption({
          animation: true,
          animationDuration: 300,
          animationEasing: 'cubicOut'
        }, false, true)
      }

      // 使用当前地图配置的城市坐标，如果没有则使用默认值
      const CITY_COORDS = this.currentMapConfig?.cityCoords || {
        '长沙市': [112.888814, 28.328209],
        '岳阳市': [112.9696, 28.9753856],
        '常德市': [111.291347, 29.101005],
        '衡阳市': [112.241, 26.8598],
        '株洲市': [113.334002, 27.527],
        '湘潭市': [112.241, 27.80009],
        '益阳市': [111.455, 28.460017],
        '邵阳市': [110.521, 26.9638],
        '张家界市': [110.2079, 29.4127],
        '怀化市': [109.628, 27.52095],
        '娄底市': [111.0628, 27.849],
        '永州市': [111.493, 25.819],
        '郴州市': [112.806, 25.827],
        '湘西土家族苗族自治州': [109.268, 28.8411],
        // 别名兼容
        '湘西州': [109.268, 28.8411],
        '湘西土家族自治州': [109.268, 28.8411],
      }

      const normalizeName = this.currentMapConfig?.normalizeName || ((nm) => {
        if (!nm) return ''
        const s = String(nm).trim()
        if (s === '湘西州' || s === '湘西土家族自治州') return '湘西土家族苗族自治州'
        return s
      })

      // 由 store 的 regions 派生图表所需数据，单位转为万元
      const cityData = (this.hunanMapData.regions || []).map(r => {
        const name = normalizeName(r.cityName)
        return {
          name,
          rank: r.ranking,
          order: (Number(r.onlineAmount) || 0) / 10000,
          retail: (Number(r.offlineAmount) || 0) / 10000,
          coord: CITY_COORDS[name]
        }
      })

      // Cache coordinate and data mappings for performance
      const coordMap = {}
      const dataByName = {}
      cityData.forEach(d => { 
        if (d.coord) coordMap[d.name] = d.coord 
        dataByName[d.name] = d
      })
      
      // 存储到缓存中供tooltip使用
      this._coordMapCache = coordMap
      this._dataByNameCache = dataByName
      
      // 数据更新时清除tooltip像素缓存
      this._tooltipPixelCache = {}
      this._tooltipHtmlCache = {}

      const fit = this.calcImageFit()
      const mapName = this.currentMapConfig?.name || 'hunan'
      
      // 重新注册地图（确保使用最新的地图数据）
      if (this.currentGeoData) {
        echarts.registerMap(mapName, this.currentGeoData);
      }

      const option = {
        backgroundColor: 'transparent',
        // 添加动画配置实现平滑过渡
        animation: true,
        animationDuration: 500,
        animationEasing: 'cubicOut',
        animationDelay: 0,
        graphic: {
          elements: [
            {
              id: 'bg-image',
              type: 'image',
              z: -100,
              left: fit.offsetX,
              top: fit.offsetY,
              style: {
                image: this.currentBgUrl || this.finalBgUrl,
                x: 0,
                y: 0,
                width: fit.drawW,
                height: fit.drawH,
                opacity: 1
              }
            }
          ]
        },
        tooltip: {
          trigger: 'item',
          // 在对应坐标点左侧显示 - 基于geo点固定位置，不跟随鼠标
          position: (pos, params, dom, rect, size) => {
            const name = params.name
            
            // 如果有可用的缓存像素坐标则使用
            const cacheKey = `${name}_${size.contentSize[0]}_${size.contentSize[1]}`
            if (this._tooltipPixelCache[cacheKey]) {
              return this._tooltipPixelCache[cacheKey]
            }
            
            const coord = this._coordMapCache[name]
            if (!coord) return pos
            
            const pixel = this.chart.convertToPixel({ geoIndex: 0 }, coord)
            const w = size.contentSize[0]
            const h = size.contentSize[1]
            const left = pixel[0] - w + 12
            const top = pixel[1] - 3 * h / 4
            
            // 缓存计算的位置
            const result = [left, top]
            this._tooltipPixelCache[cacheKey] = result
            return result
          },
          formatter: (params) => {
            const name = params.name
            
            // 使用记忆化的数据查找
            const d = this._dataByNameCache[name]
            if (!d) return ''
            
            // 记忆化昂贵的HTML字符串生成
            const cacheKey = `tooltip_${name}_${d.rank}_${d.order}_${d.retail}`
            if (this._tooltipHtmlCache && this._tooltipHtmlCache[cacheKey]) {
              return this._tooltipHtmlCache[cacheKey]
            }
            
            // 如果不存在则初始化tooltip HTML缓存
            if (!this._tooltipHtmlCache) {
              this._tooltipHtmlCache = {}
            }
            
            // 生成HTML并缓存
            const html = `<div style="width: 240px;height: 193px;color: #ffffff; line-height: 1; position:relative;box-sizing: border-box; padding:16px 35px 16px 14px;">
                <div style="position: absolute;top: 0;left: 0;bottom: 0;right: 0;background: url(${this.tooltipBgUrl}) no-repeat center center;background-size: 100% auto;"></div>
                <div style="width: 100%;height: 100%;position:relative;">
                  <div style="font-size:12px;margin-bottom:16px;">排行：<b>${d.rank}</b></div>
                  <div style="margin-bottom: 20px;">
                    <div style="display: flex; align-items: center;margin-left: 4px;margin-bottom: 10px;">
                      <span style="width:9px;height:9px;background:#136AB8;border-radius:50%;margin-right:12px;"></span>
                      <span style="font-size:16px;">征订总码洋(万元)</span>
                    </div>
                    <span style="margin-left:28px;color:#FFFD18;font-size:22px;font-weight:700;">${d.order.toFixed(2)}</span>
                  </div>
                  <div style="margin:6px 0;">
                    <div style="display: flex; align-items: center;margin-left: 4px;margin-bottom: 10px;">
                      <span style="width:9px;height:9px;background:#136AB8;border-radius:50%;margin-right:12px;"></span>
                      <span style="font-size:16px;">零售总码洋(万元)</span>
                    </div>
                    <span style="margin-left:28px;color:#04C590;font-size:22px;font-weight:700;">${d.retail.toFixed(2)}</span>
                  </div>
                </div>
              </div>`
            
            // 缓存生成的HTML
            this._tooltipHtmlCache[cacheKey] = html
            return html
          },
          extraCssText: 'background:transparent;border:none;box-shadow:none;'
        },
        geo: {
          map: mapName,
          roam: false,
          // 将像素偏移换算为百分比，确保 geo 与背景同步移动
          layoutCenter: [
            `${50 + (this.dx / fit.cw) * 100}%`,
            `${50 + (this.dy / fit.ch) * 100}%`
          ],
          layoutSize: fit.layoutSize,
          aspectScale: fit.aspectScale,
          itemStyle: {
            color: 'rgba(0,0,0,0)',
            borderColor: 'rgba(255,255,255,0.0)',
            // borderColor: '#000000',
            // borderWidth: 2
          },
          emphasis: { disabled: true },
          select: { disabled: true },
          label: { show: false }
        },
        series: [
          // 透明 map 系列用于捕获区域 hover 并触发 tooltip
          {
            name: '区域悬停',
            type: 'map',
            map: mapName,
            geoIndex: 0,
            zlevel: 1,
            tooltip: { show: true },
            itemStyle: { opacity: 0, borderColor: 'transparent' },
            emphasis: { itemStyle: { opacity: 0 }, label: { show: false } },
            select: { disabled: true },
            data: cityData.map(d => ({ name: d.name, value: d.order }))
          },
          // 悬停水波纹坐标点，仅在 hover 时写入数据
          {
            id: 'hover-point',
            name: '悬停坐标',
            type: 'effectScatter',
            coordinateSystem: 'geo',
            zlevel: 3,
            showEffectOn: 'render',
            rippleEffect: { number: 2, brushType: 'fill', scale: 3.5, period: 2 },
            symbol: 'circle',
            symbolSize: 12,
            itemStyle: { color: '#FEC03D' },
            label: { show: false },
            tooltip: { show: false },
            data: []
          }
        ]
      }

      this.chart.setOption(option)
      
      // 恢复悬停状态（如果存在）
      if (this._currentHoverState && this._currentHoverState.coord) {
        this.chart.setOption({
          series: [{
            id: 'hover-point',
            data: [{
              name: this._currentHoverState.name,
              value: this._currentHoverState.coord
            }]
          }]
        })
      }
      
      // 如果是地图切换，添加淡入动画
      if (this.isMapLoading) {
        setTimeout(() => {
          if (this.chart) {
            this.chart.setOption({
              animation: true,
              animationDuration: 300,
              animationEasing: 'cubicInOut',
              series: [{
                type: 'map',
                itemStyle: {
                  opacity: 1
                }
              }]
            })
          }
        }, 50) // 短暂延迟确保地图已渲染
      }
      
      // 再次保证背景图尺寸与画布一致
      this.updateBgGraphic()

      // 悬停事件：在地图区域鼠标移动时更新涟漪坐标；移出时隐藏
      let hoverRAF = null
      let lastHoverParams = null
      const onMouseMove = (params) => {
        if (!params || params.seriesType !== 'map') return
        lastHoverParams = params
        if (!hoverRAF) {
          hoverRAF = requestAnimationFrame(() => {
            hoverRAF = null
            const p = lastHoverParams
            if (!p) return
            const name = p.name
            const coord = this._coordMapCache[name]
            if (!coord) return
            const d = this._dataByNameCache[name]
            const value = d ? d.order : 0

            // 更新悬停状态记录
            this._currentHoverState = { name, coord }

            this.chart.setOption({
              series: [{ id: 'hover-point', type: 'effectScatter', coordinateSystem: 'geo', data: [[coord[0], coord[1], value]] }]
            })
          })
        }
      }

      const onMouseOut = (params) => {
        if (params && params.seriesType === 'map') {
          lastHoverParams = null
          // 清除悬停状态记录
          this._currentHoverState = null
          this.chart.setOption({ series: [{ id: 'hover-point', type: 'effectScatter', coordinateSystem: 'geo', data: [] }] })
        }
      }

      // 清理现有的事件监听器
      this.chart.off('mousemove')
      this.chart.off('mouseout')
      this.chart.off('globalout')
      this.chart.off('click')

      this.chart.on('mousemove', onMouseMove)
      this.chart.on('mouseout', onMouseOut)
      this.chart.on('globalout', () => {
        // 清除悬停状态记录
        this._currentHoverState = null
        this.chart.setOption({ series: [{ id: 'hover-point', type: 'effectScatter', coordinateSystem: 'geo', data: [] }] })
      })
      
      // 添加点击事件处理
      this.chart.on('click', (params) => {
        this.handleMapClick(params)
      })
    },
    updateBgGraphic() {
      if (!this.chart) return
      const fit = this.calcImageFit()
      // 背景与 geo 位置变化时，清理 tooltip 像素缓存，防止位置陈旧
      this._tooltipPixelCache = {}
      this.chart.setOption({
        graphic: {
          elements: [
            {
              id: 'bg-image',
              type: 'image',
              z: -100,
              left: fit.offsetX,
              top: fit.offsetY,
              style: {
                image: this.currentBgUrl || this.finalBgUrl,
                x: 0,
                y: 0,
                width: fit.drawW,
                height: fit.drawH
              }
            }
          ]
        },
        geo: {
          layoutCenter: [
            `${50 + (this.dx / fit.cw) * 100}%`,
            `${50 + (this.dy / fit.ch) * 100}%`
          ],
          layoutSize: fit.layoutSize,
          aspectScale: fit.aspectScale,
          itemStyle: {
            color: 'rgba(0,0,0,0)',
            borderColor: 'rgba(255,255,255,0.0)',
            // borderColor: '#000000',
            // borderWidth: 2
          },
          emphasis: { disabled: true },
          select: { disabled: true },
          label: { show: false }
        }
      })
    }
    ,
    // 预加载当前地图背景图片，确保切换时背景与动画同步显示
    preloadImage(url) {
      return new Promise((resolve) => {
        if (!url) return resolve(false)
        const img = new Image()
        img.onload = () => resolve(true)
        img.onerror = () => resolve(false)
        img.src = url
      })
    },
    async ensureBgPreloaded() {
      try {
        const url = this.finalBgUrl
        if (!url) return
        if (this.currentBgUrl === url) return
        this._bgLoading = true
        await this.preloadImage(url)
        this.currentBgUrl = url
      } finally {
        this._bgLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.hunan-map {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .map-container {
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;

    .map-chart {
      // 移除固定样式，改为通过:style动态绑定
      // 默认样式已在mapChartStyle计算属性中定义
    }
    
    .map-navigation {
      position: absolute;
      top: 20px;
      left: 30px;
      z-index: 1000;
      
      .nav-button {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        background: rgba(0, 123, 255, 0.9);
        color: white;
        border: none;
        border-radius: 25px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s ease;
        box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
        
        &:hover:not(:disabled) {
          background: rgba(0, 123, 255, 1);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 123, 255, 0.4);
        }
        
        &:active:not(:disabled) {
          transform: translateY(0);
        }
        
        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        
        .nav-icon {
          font-size: 16px;
          font-weight: bold;
        }
        
        .nav-text {
          white-space: nowrap;
        }
      }
    }
  }
}
</style>